import React, { Fragment, type JSX } from 'react';
import styled, { css } from 'styled-components';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { AWAITS_SHIPPING_APPROVAL, CANCELED, CANCELING, MANUFACTURING, PACKING, PROCESSING } from 'constants/consignmentStates';
import { FontWeight } from 'lib/styles';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { formatDateTime, currencySign } from 'utils/formatters';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import productColumns from 'constants/productColumns';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { SHIPPING } from 'constants/deliveryModes';
import { CONSIGNMENT_TYPES, PICKUP_DETAILS, SHIPPING_DETAILS, TEXT_LABELS } from 'constants/order';
import BadgePill from 'components/Settings/ProductBadges/BadgePill';
import { addonsRow, getPillTheme, rowStyle } from 'utils/consignment';
import Pill from 'components/UI/Pill';
import { useCountries } from 'hooks/useCountries';
import { navigate } from 'hooks/useHookRouterCompat';
import { getProductSkuValueField } from 'utils/orderComponents';
import AffiliateSummary from 'common/AffiliateSummary';
import AffiliateCommissionLine from 'common/AffiliateCommissionLine';
import ConsignmentEntryComments from 'components/Consignments/ConsignmentEntryComments';

const BadgeSlot = styled.span`
  align-self: flex-start;
  margin: 0px 0px 10px 0px;
`;

const ProductImage = styled.img<any>`
  cursor: pointer;
  max-height: 96px;
  max-width: 96px;
  margin: 0 auto;
  align-self: baseline;
  object-fit: contain;
`;

const TableFooterColumn = styled.span<any>`
  width: 200px;
  height: 20px;
  font-style: normal;
  font-weight: ${FontWeight.bold};
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const TableFooterData = styled.span<any>`
  width: 104px;
  height: 20px;
  text-align: right;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const Surcharges = styled.span<any>`
  font-style: italic;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: ${Text.primary};
`;

const TableColumn = styled.span<any>`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  min-height: 18px;
  width: 234px;
  color: ${Text.secondary};
  margin-bottom: 4px;

  ${({ color }) => color && css`
  color: ${color};
`}

${({ textDecoration }) => textDecoration && css`
  text-decoration: ${textDecoration};
`}

${({ fontSize }) => fontSize && css`
  font-size: ${fontSize};
`}

${({ fontWeight }) => fontWeight && css`
  font-weight: ${fontWeight};
`}

  ${({ deleted }) => deleted && css`
  opacity: 0.5;
`}

${({ width }) => width && css`
  width: ${width};
`}
`;

const TableData = styled.td<any>`
  text-align: center;
  vertical-align: top;

  &:first-child:not(:only-child):not([rowspan]) {
    text-align: left;
    font-weight: ${FontWeight.bold};
  }

  &:first-child:not(:only-child)[rowspan] {
    text-align: left;
  }

  &:last-child:not(:only-child)[rowspan] {
    padding-right: 10px;
    text-align: right;
    margin-right: 0;
    font-weight: ${FontWeight.bold};
  }

  ${({ color }) => color && css`
    color: ${color};
  `}

  ${({ textDecoration }) => textDecoration && css`
    text-decoration: ${textDecoration};
  `}

  ${({ fontSize }) => fontSize && css`
    font-size: ${fontSize};
  `}

  ${({ fontWeight }) => fontWeight && css`
    font-weight: ${fontWeight};
  `}

  ${({ deleted }) => deleted && css`
    opacity: 0.5;
  `}
`;

const Consignment = styled.span<any>`
  height: 20px;
  font-style: normal;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const ConsignmentNavigatable = styled.a<any>`
  height: 20px;
  font-style: normal;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
`;

const mapLabelForEntry = (type: string) => {
  switch (type) {
    case PROCESSING:
    case PACKING:
      return 'In stock';
    case AWAITS_SHIPPING_APPROVAL:
      return 'Shipping cost approval';
    case MANUFACTURING:
      return 'Manufacturing';
    case CANCELED:
    case CANCELING:
      return 'Canceled';
    default:
      return '';
  }
}

interface TableValue {
  product: JSX.Element,
  quantity: JSX.Element,
  unitPrice: JSX.Element,
  subTotal: JSX.Element,
  afterRowContent?: JSX.Element
};

interface Props {
  consignmentEntries: ConsignmentEntry[];
  isPreparationStep?: boolean;
  totalDeliveryCost?: number;
  totalPriceWithTax?: number;
  state: string;
  showSummary?: boolean;
  subTotal?: number;
  deliveryDate?: string;
  isRental?: boolean;
  consignmentId?: string;
  deliveryTax?: number;
  deliveryCost?: number;
  orderTotalCost?: number;
  fulfillmentRequest?: ConsignmentEntry[];
  shippingOptions?: string[];
  canNavigateToOrderDetails?: boolean;
  vendorName?: string;
  countryId?: number;
  consignmentSubtotal?: number;
  consignmentTotalTax?: number;
  consignmentTotalSurcharges?: number;
  consignmentTotalDiscounts?: number;
};

const OrderPreviewItem = ({
  consignmentEntries,
  state,
  showSummary = true,
  deliveryDate,
  isRental,
  consignmentId,
  deliveryCost = 0,
  deliveryTax = 0,
  orderTotalCost = 0,
  fulfillmentRequest,
  shippingOptions = [],
  canNavigateToOrderDetails = false,
  vendorName,
  countryId,
  consignmentSubtotal,
  consignmentTotalTax,
  consignmentTotalSurcharges,
  consignmentTotalDiscounts
}: Props) => {

  let orderSubTotalPrice = 0;
  let totalTax = 0;
  let totalSurcharges = 0;

  const { countries } = useCountries();

  const country = countries.find(x => x.id === countryId)?.countryIso ?? null;

  const productColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {
    const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;
    const pricePerUnit = consignmentEntry?.basePrice;

    const mappedEntry = fulfillmentRequest?.find(x => consignmentEntry.entryNumber === x.entryNumber);
    const rentalStartDate = mappedEntry ? mappedEntry?.rentalStartDate : consignmentEntry.rentalStartDate;
    const rentalEndDate = mappedEntry ? mappedEntry?.rentalEndDate : consignmentEntry.rentalEndDate;

    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent flexDirection='row'>
          <FlexboxContainerComponent padding='0 12px 0 0' flexDirection='column'>
            {
              consignmentEntry.badge &&
              <BadgeSlot>
                <BadgePill name={consignmentEntry.badge.name} colorHash={consignmentEntry.badge.colorHash} />
              </BadgeSlot>
            }
            <ProductImage src={consignmentEntry.productImages && consignmentEntry.productImages.length > 0 ? process.env.REACT_APP_MEDIA_URL! + consignmentEntry.productImages[0].path : ''} />
          </FlexboxContainerComponent>
          <FlexboxContainerComponent flexDirection='column'>
            <FlexboxContainerComponent justifyContent='flex-start' margin='0 0 12px' padding='0px 0 0'>
              <TableData>{consignmentEntry.productName}</TableData>
            </FlexboxContainerComponent>
            <FlexboxContainerComponent flexDirection='row'>
              <FlexboxContainerComponent flexDirection='column' gap='6px'>
                {getProductSkuValueField(consignmentEntry, state!)}
                {
                  isRental &&
                  <FlexboxContainerComponent>
                    <TableColumn>Serial number</TableColumn>
                    <TableColumn color={Text.primary}>{consignmentEntry.serialNumber || 'N/A'}</TableColumn>
                  </FlexboxContainerComponent>
                }
                {
                  consignmentEntry.selectableAttributes && consignmentEntry.selectableAttributes.map(atr => {
                    const atrValue = consignmentEntry.attributeValues.find(val => val.attributeId === atr.id);
                    return (
                      <FlexboxContainerComponent gap='2px !important'><TableColumn>{atr.name}</TableColumn><TableColumn color={Text.primary}>{atrValue ? atrValue.name : ''}</TableColumn></FlexboxContainerComponent>
                    )
                  })
                }
                {
                  consignmentEntry.pricebookName && consignmentEntry.pricebookId &&
                  <FlexboxContainerComponent>
                    <TableColumn>Pricebook name / ID</TableColumn>
                    <TableColumn color={Text.primary}>
                      {`${consignmentEntry.pricebookName} / ${consignmentEntry?.priceBook?.PricebookVendorId}`}
                    </TableColumn>
                  </FlexboxContainerComponent>
                }
                {
                  deliveryDate &&
                  <FlexboxContainerComponent>
                    <TableColumn>{consignmentEntries[0]?.deliveryMode === SHIPPING ? "Est. ship date" : "Est. pickup date"}</TableColumn>
                    <TableColumn color={Text.primary}>{formatDateTime(deliveryDate, false, false)}</TableColumn>
                  </FlexboxContainerComponent>
                }


                {rentalStartDate && rentalEndDate && isRental &&
                  < FlexboxContainerComponent >
                    <TableColumn>Date range</TableColumn>
                    <TableColumn color={Text.primary}>{formatDateTime(rentalStartDate, false, false) + " - " + formatDateTime(rentalEndDate, false, false)}</TableColumn>
                  </FlexboxContainerComponent>
                }

                {isRental && pricePerUnit && unitOfMeasurement &&
                  <FlexboxContainerComponent>
                    <TableColumn>Price rate</TableColumn>
                    <TableColumn color={Main.brandDarkest} fontWeight='550'>{`${currencySign(country, pricePerUnit!)} / ${unitOfMeasurement}`}</TableColumn>
                  </FlexboxContainerComponent>
                }
                {
                  consignmentEntry.affiliateCode && consignmentEntry.commissionPercentage != null &&
                  <AffiliateCommissionLine
                    commissionPercentage={consignmentEntry.commissionPercentage}
                    commissionAmount={consignmentEntry.commissionAmount || 0}
                    customerAffiliate={consignmentEntry.customerAffiliate}
                    country={country}
                  />
                }

              </FlexboxContainerComponent>

            </FlexboxContainerComponent>
          </FlexboxContainerComponent>
        </FlexboxContainerComponent>
      </Fragment >
    )
  });

  const quantityColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {
    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent justifyContent='flex-end' >
          <TableData>{consignmentEntry?.quantity}</TableData>
        </FlexboxContainerComponent>
      </Fragment>
    )
  });

  const unitPriceColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {

    const unitPrice = consignmentEntry.basePrice !== consignmentEntry.discountedPrice ? consignmentEntry.discountedPrice : consignmentEntry.basePrice;

    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          {
            <TableData>{currencySign(country, unitPrice)}</TableData>
          }
        </FlexboxContainerComponent>
      </Fragment>
    )
  });

  const subTotalColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {
    if (consignmentTotalTax) {
      totalTax = consignmentTotalTax;
    } else {
      totalTax += consignmentEntry?.taxAmount || 0;
    }

    if (consignmentTotalSurcharges) {
      totalSurcharges = consignmentTotalSurcharges;
    } else {
      totalSurcharges += consignmentEntry?.surcharge || 0;
    }

    if (consignmentSubtotal) {
      orderSubTotalPrice = consignmentSubtotal;
    } else {
      orderSubTotalPrice += consignmentEntry.totalPrice || 0;
    }



    const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;

    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          <TableData>{currencySign(country, consignmentEntry.totalPrice)}</TableData>
        </FlexboxContainerComponent>
        <FlexboxContainerComponent justifyContent='flex-end' padding='16px 0 0 '>
          {
            consignmentEntry.surcharge > 0
              ? <TableData rowSpan={2}>+{currencySign(country, consignmentEntry.surcharge)} <br /> surcharge</TableData>
              : <TableData>&nbsp;</TableData>
          }
        </FlexboxContainerComponent>
        <FlexboxContainerComponent justifyContent='flex-end' padding='16px 0 0 '>
          {
            isRental && consignmentEntry.plannedUsage > 0 &&
            <TableData fontWeight={FontWeight.bold} rowSpan={2}>{`${TEXT_LABELS.PLANNED_USAGE}: ${consignmentEntry.plannedUsage} ${unitOfMeasurement}`}</TableData>
          }
        </FlexboxContainerComponent>
      </Fragment>
    )
  });

  const data: TableValue[] = [];

  consignmentEntries.filter(x => x.parentEntryNumber === null).forEach((c, index) => {
    const addonsEntries = consignmentEntries.filter(x => x.parentEntryNumber === c.entryNumber);
    let requiredAddons = addonsEntries.filter(x => x.requiredAddon);
    let optionalAddons = addonsEntries.filter(x => !x.requiredAddon);

    const requiredAddonsFragmentRows = addonsRow(requiredAddons, true, state!, country!);
    const optionalAddonsFragmentRows = addonsRow(optionalAddons, false, state!, country!);

    data.push({
      product: productColumn[index],
      quantity: quantityColumn[index],
      unitPrice: unitPriceColumn[index],
      subTotal: subTotalColumn[index],
      afterRowContent: c.comments && c.comments.length > 0
        ? <ConsignmentEntryComments comments={c.comments} />
        : undefined
    });

    data.push(...requiredAddonsFragmentRows);
    data.push(...optionalAddonsFragmentRows);
  });

  const orderTotal = showSummary && (
    <FlexboxContainerComponent flexDirection="column" alignItems='flex-end' width='100%' padding='16px'>
      <FlexboxContainerComponent padding='16px'>
        <TableFooterColumn>Subtotal</TableFooterColumn>
        <TableFooterData> {orderSubTotalPrice && currencySign(country, orderSubTotalPrice)}</TableFooterData>
      </FlexboxContainerComponent>
      {
        consignmentTotalDiscounts && consignmentTotalDiscounts > 0
          ? (
            <FlexboxContainerComponent padding='16px'>
              <Surcharges>includes savings</Surcharges>
              <TableFooterData> -{consignmentTotalDiscounts && currencySign(country, consignmentTotalDiscounts)}</TableFooterData>
            </FlexboxContainerComponent>
          )
          : null
      }
      {
        totalSurcharges && totalSurcharges > 0
          ? (
            <FlexboxContainerComponent padding='16px'>
              <Surcharges>includes surcharges</Surcharges>
              <TableFooterData> +{totalSurcharges && currencySign(country, totalSurcharges)}</TableFooterData>
            </FlexboxContainerComponent>
          )
          : null
      }
      {
        deliveryCost ? (
          <FlexboxContainerComponent padding='16px' borderTop={`1px solid ${Border.light}`} borderBottom={`1px solid ${Border.light}`}>
            <TableFooterColumn fontSize="15px">Shipping</TableFooterColumn>
            <TableFooterData fontSize="15px">{currencySign(country, deliveryCost)}</TableFooterData>
          </FlexboxContainerComponent>
        ) : null
      }
      {
        totalTax ? (
          <FlexboxContainerComponent borderTop='1px solid #E1E6EB' padding='16px'>
            <TableFooterColumn>Tax</TableFooterColumn>
            <TableFooterData> {totalTax && currencySign(country, totalTax + deliveryTax)}</TableFooterData>
          </FlexboxContainerComponent>
        ) : null
      }
      <FlexboxContainerComponent borderTop={`2px solid ${Background.navy}`} padding='16px'>
        <TableFooterColumn>Order total</TableFooterColumn>
        <TableFooterData> {currencySign(country, orderTotalCost)}</TableFooterData>
      </FlexboxContainerComponent>
    </FlexboxContainerComponent>
  )

  const pillTheme = getPillTheme(state);

  const pill = <Pill margin="10px 10px 10px 0px" theme={pillTheme} bgColor={pillTheme} size="xsPlus" padding="4px">{mapLabelForEntry(state)}</Pill>;

  const consignmentEntry = fulfillmentRequest?.find(x => x.parentEntryNumber === null && consignmentEntries.find(y => y.entryNumber === x.entryNumber));
  const rentalStartDate = consignmentEntry ? consignmentEntry?.rentalStartDate : consignmentEntries[0]?.rentalStartDate;
  const rentalEndDate = consignmentEntry ? consignmentEntry?.rentalEndDate : consignmentEntries[0]?.rentalEndDate;

  const isShipping = consignmentEntries[0]?.deliveryMode === SHIPPING;
  const shippingDetails = isShipping ? SHIPPING_DETAILS : PICKUP_DETAILS;

  const handleNavigateConsignment = () => {
    navigate(`/orders/order-details/${consignmentId?.slice(4)}`, false);
  };

  return (
    <>
      <FlexboxContainerComponent flexDirection='column' gap='10px' margin='0px'>
        <FlexboxContainerComponent flexDirection='row' gap='10px' margin='0px' alignItems='center'>
          {
            canNavigateToOrderDetails ?
              <ConsignmentNavigatable onClick={handleNavigateConsignment}>Order # {consignmentId?.slice(4)}</ConsignmentNavigatable> :
              <Consignment>Order # {consignmentId?.slice(4)}</Consignment>
          }
          {pill}
          {state !== CANCELED && deliveryDate && <FlexboxContainerComponent alignItems='center'>
            <TableColumn color={Text.primary} width={'134px'}>{isShipping ? "Est. ship date" : "Est. pickup date"}</TableColumn>
            <TableColumn color={Text.primary} width={'134px'}>{formatDateTime(deliveryDate, false, false)}</TableColumn>
          </FlexboxContainerComponent>
          }
        </FlexboxContainerComponent>
        {
          vendorName &&
          <FlexboxContainerComponent alignItems='center'>
            <TableColumn color={Text.primary} width={'100px'}>Vendor</TableColumn>
            <TableColumn color={Text.primary} width={'200px'} fontWeight={FontWeight.bold}>{vendorName}</TableColumn>
          </FlexboxContainerComponent>
        }
        {
          isRental &&
          <>
            <FlexboxContainerComponent alignItems='center'>
              <TableColumn color={Text.primary} width={'100px'}>Start rent date</TableColumn>
              <TableColumn color={Text.primary} width={'134px'} fontWeight={FontWeight.bold} >{formatDateTime(rentalStartDate, false, false)}</TableColumn>
            </FlexboxContainerComponent>
            <FlexboxContainerComponent alignItems='center'>
              <TableColumn color={Text.primary} width={'100px'}>End rent date</TableColumn>
              <TableColumn color={Text.primary} width={'134px'} fontWeight={FontWeight.bold} >{formatDateTime(rentalEndDate, false, false)}</TableColumn>
            </FlexboxContainerComponent>
          </>
        }
        {
          shippingOptions.length > 0 &&
          <>
            <FlexboxContainerComponent alignItems='center'>
              <TableColumn color={Text.primary} width={'100px'} fontWeight={FontWeight.bold}>{isShipping ? TEXT_LABELS.SHIPPING : TEXT_LABELS.PICKUP}</TableColumn>
            </FlexboxContainerComponent>
            {
              shippingDetails.map((label, index) => (
                <FlexboxContainerComponent alignItems='center'>
                  <TableColumn color={Text.primary} width={'120px'}>{label}</TableColumn>
                  <TableColumn color={Text.primary} width={'500px'}>{shippingOptions[index]}</TableColumn>
                </FlexboxContainerComponent>
              ))
            }
          </>
        }
      </FlexboxContainerComponent>
      <FlexboxContainerComponent padding='0 0' justifyContent="space-between" alignItems="center">
        <p>{'Order items'}</p>
      </FlexboxContainerComponent>
      <GenericTableWrapper
        columns={productColumns}
        data={data}
        toolbar
        overrideToolbar
        borderBetweenRows={false}
        wrapperWidth="100%"
      />
      {orderTotal}
      {consignmentEntries.some(e => !!e.affiliateCode && e.commissionPercentage != null) && (
        <AffiliateSummary
          country={country}
          totalCommission={consignmentEntries.reduce((sum, e) => sum + (e.commissionAmount || 0), 0)}
        />
      )}
    </>
  );
};

export default OrderPreviewItem;
